import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChevronLeft } from "lucide-react";

const TableWrapper = ({ children }) => (
  <div className="overflow-x-auto my-4">
    <table className="w-full text-sm border-collapse">
      {children}
    </table>
  </div>
);

const Th = ({ children }) => <th className="border border-border bg-muted px-3 py-2 text-left font-semibold">{children}</th>;
const Td = ({ children }) => <td className="border border-border px-3 py-2">{children}</td>;

export default function Rules() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    { id: "about", title: "About Junk Ball" },
    { id: "components", title: "Components" },
    { id: "setup", title: "Game Setup" },
    { id: "modifiers", title: "Modifiers" },
    { id: "manager", title: "Manager Cards" },
    { id: "pitching", title: "Pitching" },
    { id: "batting", title: "Batting & Hits" },
    { id: "fielding", title: "Fielding" },
    { id: "baserunning", title: "Base Running" },
    { id: "terminology", title: "Terminology" },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-bold">Junk Ball Rules</h1>
          <div className="w-20" />
        </div>
        {/* Quick Nav */}
        <div className="max-w-5xl mx-auto px-4 pb-2 flex gap-2 overflow-x-auto text-xs">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`px-2 py-1 rounded whitespace-nowrap transition-colors ${activeSection === s.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
            >
              {s.title}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* About */}
        <Card id="about">
          <CardHeader><CardTitle>⚾ About Junk Ball</CardTitle></CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none">
            <p>Junk Ball is a game made for ripping packs, scouring cards, building dream lineups, and letting iconic moments unfold across the field. With Junk Ball, every card in your collection becomes a playable piece.</p>
            <p>Draft your squad from cards you already own — any era, any brand, as long as they have stats on the back — or crack open a box and trade with friends to create never-before-seen matchups.</p>
            <p>Junk Ball mixes dice rolls with real player stats and unique manager cards turning pitcher and batter duels, base running, and defensive plays into a tug of war between luck and strategy.</p>
            <p className="text-muted-foreground italic">In Junk Ball, every card deserves its day.</p>
            
            <div className="grid grid-cols-3 gap-4 mt-4 text-center">
              <div className="bg-muted rounded-lg p-3">
                <div className="text-2xl font-bold">2-4</div>
                <div className="text-xs text-muted-foreground">Players</div>
              </div>
              <div className="bg-muted rounded-lg p-3">
                <div className="text-2xl font-bold">12+</div>
                <div className="text-xs text-muted-foreground">Age</div>
              </div>
              <div className="bg-muted rounded-lg p-3">
                <div className="text-2xl font-bold">5-9</div>
                <div className="text-xs text-muted-foreground">Innings</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Components */}
        <Card id="components">
          <CardHeader><CardTitle>🎲 Components</CardTitle></CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Bare Minimum Needed:</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>Manager Cards</li>
                  <li>Pitch Chips x 4 (2 sets)</li>
                  <li>Baseball Cards (with season stats)</li>
                  <li>D10 x 2 (batter vs pitcher rolls)</li>
                  <li>D6 x 2 (fielding and base running)</li>
                  <li>D20 x 1</li>
                  <li>LCR x 1 (directional hitting)</li>
                  <li>Paper and pen</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Optional (Enhanced):</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>Official Junk Ball Board</li>
                  <li>New Box of Cards (any era)</li>
                  <li>Scorebook</li>
                  <li>Card stands</li>
                  <li>Penny/hard sleeves</li>
                  <li>Sunflower seeds 🌻</li>
                  <li>Big League Chew</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Game Setup */}
        <Card id="setup">
          <CardHeader><CardTitle>🏟️ Game Setup</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="variants">
                <AccordionTrigger>Game Variants</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <div className="bg-muted p-3 rounded">
                      <div className="font-semibold">Clean Game (Beginners)</div>
                      <ul className="text-sm list-disc list-inside mt-1">
                        <li>No season stat modifiers</li>
                        <li>Manager Card effects apply</li>
                        <li>No pitch chips</li>
                      </ul>
                    </div>
                    <div className="bg-muted p-3 rounded">
                      <div className="font-semibold">Enhanced</div>
                      <ul className="text-sm list-disc list-inside mt-1">
                        <li>Season stat modifiers applied</li>
                        <li>Manager Card effects apply</li>
                        <li>Pitch chips active</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="draft">
                <AccordionTrigger>Draft Rules</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mb-2">Flip a coin to decide who drafts first. Take turns selecting cards:</p>
                  <ul className="text-sm list-disc list-inside space-y-1">
                    <li>Pick from loose card pile, OR</li>
                    <li>Open a pack and select one player</li>
                    <li>Continue until rosters are full</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="roster">
                <AccordionTrigger>Roster Size (up to 26)</AccordionTrigger>
                <AccordionContent>
                  <ul className="text-sm list-disc list-inside space-y-1">
                    <li>1–2 Catchers</li>
                    <li>5 Infielders (2+ utility)</li>
                    <li>4 Outfielders</li>
                    <li>2–3 Pitchers</li>
                    <li>1 Manager Card (optional)</li>
                  </ul>
                  <p className="text-sm mt-2 text-muted-foreground">Once subbed out, a player is out for the game.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Modifiers */}
        <Card id="modifiers">
          <CardHeader><CardTitle>📊 Modifiers</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Max stacked modifier: +2/-2 for any category. Recommended: 100+ games played (20-25 for pitchers).</p>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="batting">
                <AccordionTrigger>Batting Modifiers</AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <div className="font-semibold text-sm mb-2">Batting Average</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>Below .240</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>.240 - .279</Td><Td>0</Td></tr>
                          <tr><Td>.280+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">Home Runs</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>0 - 14</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>15 - 29</Td><Td>0</Td></tr>
                          <tr><Td>30+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">RBI</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>0 - 49</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>50 - 89</Td><Td>0</Td></tr>
                          <tr><Td>90+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="speed">
                <AccordionTrigger>Speed / Base Running</AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <div className="font-semibold text-sm mb-2">Stolen Bases</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>0 - 9</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>10 - 39</Td><Td>0</Td></tr>
                          <tr><Td>40+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">Doubles</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>0 - 9</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>10 - 39</Td><Td>0</Td></tr>
                          <tr><Td>40+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">Triples</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>0 - 9</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>10 - 39</Td><Td>0</Td></tr>
                          <tr><Td>40+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="pitching-mods">
                <AccordionTrigger>Pitching Modifiers</AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="font-semibold text-sm mb-2">ERA</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>4.00+</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>3.00 – 3.99</Td><Td>0</Td></tr>
                          <tr><Td>2.99 and below</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">Strikeouts</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>169 and below</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>170 - 199</Td><Td>0</Td></tr>
                          <tr><Td>200+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">Wins</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>0 – 5</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>6 – 15</Td><Td>0</Td></tr>
                          <tr><Td>16+</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-2">Losses</div>
                      <TableWrapper>
                        <tbody>
                          <tr><Td>15+</Td><Td className="text-red-500">-1</Td></tr>
                          <tr><Td>9 – 14</Td><Td>0</Td></tr>
                          <tr><Td>8 and below</Td><Td className="text-green-500">+1</Td></tr>
                        </tbody>
                      </TableWrapper>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Manager Cards */}
        <Card id="manager">
          <CardHeader><CardTitle>🎩 Manager Cards</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>Manager cards represent veteran savvy and influence — wild dugout antics, bold coaching calls, and pure bedlam.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-muted p-3 rounded">
                <div className="font-semibold">Playing Cards</div>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Flip face-up anytime during at-bats</li>
                  <li>Card stays face-up until end of inning</li>
                  <li>Max 4 cards total (active or in-hand)</li>
                  <li>Max 4 cards played per inning</li>
                </ul>
              </div>
              <div className="bg-muted p-3 rounded">
                <div className="font-semibold">Card Types</div>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><span className="text-muted-foreground">Common:</span> Simple boosts (+1s, rerolls)</li>
                  <li><span className="text-blue-400">Rare:</span> Tactical plays (steals, swaps)</li>
                  <li><span className="text-purple-400">Iconic:</span> Game-changers</li>
                  <li><span className="text-yellow-400">Career Game:</span> Legends from nobodies</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pitching */}
        <Card id="pitching">
          <CardHeader><CardTitle>⚾ Pitching</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="font-semibold mb-2">Basic Pitch Resolution</div>
              <ol className="list-decimal list-inside text-sm space-y-1">
                <li>Both pitcher and batter roll D10</li>
                <li>Add respective modifiers</li>
                <li>Compare totals — higher wins</li>
              </ol>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
                <div className="bg-background rounded p-2"><div className="font-bold">Pitcher Wins</div><div className="text-muted-foreground">Strike</div></div>
                <div className="bg-background rounded p-2"><div className="font-bold">Batter Wins</div><div className="text-muted-foreground">Ball in Play</div></div>
                <div className="bg-background rounded p-2"><div className="font-bold">Tie</div><div className="text-muted-foreground">Foul Ball</div></div>
              </div>
            </div>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="chips">
                <AccordionTrigger>Pitch Chips</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mb-2">Each pitcher has 2-4 pitch types. Before each pitch, pitcher places chip face-down. Batter guesses or calls ball.</p>
                  <ul className="text-sm list-disc list-inside">
                    <li><strong>Chip win:</strong> +1 to the winning side's roll</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="got-your-number">
                <AccordionTrigger>Got Your Number (advanced)</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm">
                    <p>
                      Each pitcher is secretly assigned <strong>1 / 1 / 2 / 3</strong> across their
                      four pitch types. Keep the sheet hidden — the whole game is your opponent
                      working it out over the course of an at-bat.
                    </p>
                    <div>
                      <p className="font-bold mb-1">Winning a pitch</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>The pot is <strong>the higher of the two Pitch Chips</strong> — the one thrown and the one guessed.</li>
                        <li><strong>Batter guesses right:</strong> the batter takes the pot.</li>
                        <li><strong>Batter guesses wrong:</strong> the pitcher takes the pot.</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-bold mb-1">Where each number comes from</p>
                      <p>
                        The pitch thrown is worth whatever the <strong>pitcher's</strong> sheet says.
                        Your guess is worth whatever <strong>your own current pitcher's</strong> sheet
                        says — your lineup hits the pitches your staff throws best.
                      </p>
                      <p className="mt-1">
                        So your guess sets a <strong>floor</strong> on what the pitcher collects when
                        you miss. Hunting with a pitch you value at 3 hands them a 3 every time you
                        are wrong. The strongest guess is a pitch that is big on <em>their</em> sheet
                        and small on <em>yours</em>.
                      </p>
                    </div>
                    <div>
                      <p className="font-bold mb-1">Going to the bullpen</p>
                      <p>
                        A new pitcher brings a new sheet, which wipes out whatever read your opponent
                        had built. It also changes what <strong>your own hitters'</strong> chips are
                        worth, because they now inherit the new pitcher's numbers — so a change is
                        both a defensive reset and an offensive decision.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="special">
                <AccordionTrigger>Special Situations</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm">
                    <div><strong>Hit By Pitch:</strong> Both roll 1 (before mods) → Batter takes first, runners advance</div>
                    <div><strong>Wild Pitch:</strong> Both roll 10 (before mods) → All runners advance one base</div>
                    <div><strong>Intentional Walk:</strong> Declare before any roll → Batter takes first</div>
                    <div><strong>Two HBPs:</strong> Same pitcher hits 2 batters → Pitcher ejected</div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="pickoff">
                <AccordionTrigger>Pick Off Attempts</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mb-2">Pitcher rolls D10, no modifiers:</p>
                  <ul className="text-sm list-disc list-inside">
                    <li><strong>9-10:</strong> Pickoff successful</li>
                    <li><strong>Big Lead declared:</strong> 8-10 successful</li>
                    <li><strong>Penalty:</strong> 3 failed attempts = -2 for current batter</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Batting & Hits */}
        <Card id="batting">
          <CardHeader><CardTitle>🏏 Batting & Hits</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="font-semibold mb-2">Hit Power = Batter Roll − Pitcher Roll (after mods)</div>
              <TableWrapper>
                <thead><tr><Th>Difference</Th><Th>Outcome</Th></tr></thead>
                <tbody>
                  <tr><Td>1 – 2</Td><Td>Weak Hit</Td></tr>
                  <tr><Td>3 – 4</Td><Td>Medium Hit</Td></tr>
                  <tr><Td>5 – 6</Td><Td>Powerful Hit</Td></tr>
                  <tr><Td>7+</Td><Td className="text-yellow-400 font-bold">Home Run</Td></tr>
                </tbody>
              </TableWrapper>
            </div>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="fielding-result">
                <AccordionTrigger>Hit vs Fielder Results</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mb-2">Fielder rolls D6 + modifier. Compare to Hit Power:</p>
                  <TableWrapper>
                    <thead><tr><Th>Hit Power − Fielder Roll</Th><Th>Result</Th></tr></thead>
                    <tbody>
                      <tr><Td>7+</Td><Td className="text-yellow-400">Home Run (all score)</Td></tr>
                      <tr><Td>5 - 6</Td><Td>Triple (+3 bases)</Td></tr>
                      <tr><Td>3 - 4</Td><Td>Double (+2 bases)</Td></tr>
                      <tr><Td>1 - 2</Td><Td>Single (+1 base)</Td></tr>
                      <tr><Td>0 / Tie</Td><Td>Single (+1 base)</Td></tr>
                      <tr><Td>-1 or lower</Td><Td className="text-red-400">Out</Td></tr>
                      <tr><Td>-2 or lower (runner on 1st)</Td><Td className="text-red-400">Double Play</Td></tr>
                    </tbody>
                  </TableWrapper>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="lcr">
                <AccordionTrigger>Directional Hitting (LCR)</AccordionTrigger>
                <AccordionContent>
                  <TableWrapper>
                    <thead><tr><Th>Roll</Th><Th>Direction</Th><Th>Fielder Rules</Th></tr></thead>
                    <tbody>
                      <tr><Td>L</Td><Td>Left</Td><Td>1-4 power: 3B/SS | 5-6: LF</Td></tr>
                      <tr><Td>C</Td><Td>Center</Td><Td>1-4 power: 2B/SS | 5-6: CF</Td></tr>
                      <tr><Td>R</Td><Td>Right</Td><Td>1-4 power: 1B/2B | 5-6: RF</Td></tr>
                      <tr><Td>Dot</Td><Td>Wild Card</Td><Td>Batter chooses (5-6 must be OF)</Td></tr>
                    </tbody>
                  </TableWrapper>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="bunts">
                <AccordionTrigger>Bunting & Sacrifice Flies</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Sacrifice Bunt:</strong> D10 roll, no mods
                      <ul className="list-disc list-inside ml-4">
                        <li>6+ = Success, batter out, runners advance</li>
                        <li>5 or less = Failed, defense chooses out</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Bunt for Hit:</strong> D10 + Speed
                      <ul className="list-disc list-inside ml-4">
                        <li>8+ = Safe at first</li>
                        <li>5-7 = Roll-off vs fielder</li>
                        <li>4 or less = Out</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Sacrifice Fly:</strong> D10, no mods
                      <ul className="list-disc list-inside ml-4">
                        <li>6+ = Runner on 3rd scores, batter out</li>
                        <li>5 or less = Runner holds, batter out</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Fielding */}
        <Card id="fielding">
          <CardHeader><CardTitle>🧤 Fielding</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="dp">
                <AccordionTrigger>Double Plays</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm">If there's a force play, runner on first, and Hit Power minus Fielder Roll is -2 or worse → Double Play. Non-forced runners on 2nd/3rd are held.</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="rundown">
                <AccordionTrigger>Rundowns</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mb-2">Tie during fielding roll (not at first) = rundown:</p>
                  <ul className="text-sm list-disc list-inside">
                    <li>Both roll D6, fielder rolls twice per runner's one</li>
                    <li>Before each roll, guess your number</li>
                    <li>First correct guess wins</li>
                    <li>Fielder guesses right = out</li>
                    <li>Runner guesses right = safe</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="position">
                <AccordionTrigger>Out of Position Penalty</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm">If a player is fielding a position not listed on their card, they get <span className="text-red-400">-1 to fielding rolls</span>. Utility players are gold!</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Base Running */}
        <Card id="baserunning">
          <CardHeader><CardTitle>🏃 Base Running</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
              <div className="font-semibold mb-2">Advancing on Hits</div>
              <ul className="list-disc list-inside">
                <li><strong>Single:</strong> Runners +1 base</li>
                <li><strong>Double:</strong> Runners +2 bases</li>
                <li><strong>Triple / HR:</strong> All runners score</li>
              </ul>
            </div>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="steal">
                <AccordionTrigger>Stealing Bases</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mb-2">Runner declares steal. Both roll D6 + mods:</p>
                  <ul className="text-sm list-disc list-inside">
                    <li>Runner higher = Safe</li>
                    <li>Catcher higher = Out</li>
                    <li>Tie = Rundown</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="leads">
                <AccordionTrigger>Lead Offs</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm">Take a lead for <span className="text-green-400">+1 to steal attempts</span> (stacks with speed, max +2). Must redeclare after pickoff attempts.</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="tagup">
                <AccordionTrigger>Tagging Up / Extra Bases</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm">After any play resolves, runners can attempt extra bases. Runner vs Fielder roll with mods. Higher wins, tie = rundown.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Terminology */}
        <Card id="terminology">
          <CardHeader><CardTitle>📖 Terminology</CardTitle></CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><strong>D6/D10/D20:</strong> 6/10/20-sided dice</div>
              <div><strong>LCR:</strong> Left-Center-Right directional die</div>
              <div><strong>Hit Power:</strong> Batter roll − Pitcher roll (after mods)</div>
              <div><strong>BA:</strong> Batting Average</div>
              <div><strong>HR:</strong> Home Runs</div>
              <div><strong>RBI:</strong> Runs Batted In</div>
              <div><strong>SB:</strong> Stolen Bases</div>
              <div><strong>ERA:</strong> Earned Run Average</div>
              <div><strong>K:</strong> Strikeouts</div>
              <div><strong>Active Card:</strong> Face-up, effects in play</div>
              <div><strong>Resolved:</strong> Effects done, awaiting discard</div>
              <div><strong>Modifier:</strong> +1/0/-1 added to rolls</div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 text-muted-foreground text-sm">
          <p className="italic">"Every card deserves its day."</p>
          <p className="mt-2">Junk Ball: The Tabletop Baseball Simulation Game</p>
        </div>
      </div>
    </div>
  );
}
